"use client"

import { useState, useRef, useEffect } from "react"
import { Calendar } from "lucide-react"

interface Props {
  title: string
  startTime: string // ISO datetime (absolute UTC instant) or bare YYYY-MM-DD
  endTime: string   // same
  location?: string
  description?: string
  // IANA zone the event takes place in (event.timezone). The datetimes are
  // already correct absolute instants; the zone tells Google's template to
  // present the event in its local wall time rather than the viewer's.
  timezone?: string | null
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

// RFC 5545 TEXT escaping: backslash, semicolon, comma, newline
const escapeIcsText = (text: string) =>
  text.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n")

// UTC basic format YYYYMMDDTHHmmssZ — a correct absolute instant that every
// calendar client localizes for its viewer
const toUtcBasic = (dateStr: string) => {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ""
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"
}

const compactDate = (dateStr: string) => dateStr.replace(/-/g, "")

// All-day END values are exclusive in every target format
const nextDayCompact = (dateStr: string) => {
  const d = new Date(`${dateStr}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10).replace(/-/g, "")
}

export default function EventCalendarButton({ title, startTime, endTime, location, description, timezone }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // 🔹 Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // A bare date (event.date fallback when no start_datetime exists) must be an
  // all-day event — parsing it as a datetime lands on UTC midnight, which
  // shifts the day for anyone east of Greenwich.
  const isAllDay = DATE_ONLY.test(startTime)

  const start = isAllDay ? compactDate(startTime) : toUtcBasic(startTime)
  const end = isAllDay
    ? nextDayCompact(DATE_ONLY.test(endTime) ? endTime : startTime)
    : toUtcBasic(endTime)

  const safeLocation = location || ""
  const safeDescription = description || ""

  // 🔹 URLs for different calendar platforms
  const googleUrl =
    `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}` +
    `&dates=${start}/${end}` +
    `&details=${encodeURIComponent(safeDescription)}` +
    `&location=${encodeURIComponent(safeLocation)}` +
    (!isAllDay && timezone ? `&ctz=${encodeURIComponent(timezone)}` : "")

  const outlookUrl =
    `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}` +
    `&body=${encodeURIComponent(safeDescription)}` +
    `&location=${encodeURIComponent(safeLocation)}` +
    `&startdt=${encodeURIComponent(startTime)}&enddt=${encodeURIComponent(isAllDay && !DATE_ONLY.test(endTime) ? startTime : endTime)}` +
    (isAllDay ? "&allday=true" : "")

  const yahooUrl =
    `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(title)}` +
    `&st=${start}&et=${end}` +
    `&desc=${encodeURIComponent(safeDescription)}` +
    `&in_loc=${encodeURIComponent(safeLocation)}`

  // 🔹 iCal file generator — UID/DTSTAMP generated per download for validity
  const handleICalDownload = () => {
    const uid = `${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Date.now()}@pathcollective`
    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Path Collective//Events//EN",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${toUtcBasic(new Date().toISOString())}`,
      `SUMMARY:${escapeIcsText(title)}`,
      isAllDay ? `DTSTART;VALUE=DATE:${start}` : `DTSTART:${start}`,
      isAllDay ? `DTEND;VALUE=DATE:${end}` : `DTEND:${end}`,
      `LOCATION:${escapeIcsText(safeLocation)}`,
      `DESCRIPTION:${escapeIcsText(safeDescription)}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n")

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `${title.replace(/[^\w\s-]/g, "").trim() || "event"}.ics`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  return (
    <div ref={ref} className="relative inline-block">
      {/* Calendar Icon */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
        className="p-2.5 rounded-full hover:bg-[#F7F0E6] transition"
        title="Add to Calendar"
      >
        <Calendar className="h-5 w-5 text-[#5E5D59]" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-52 bg-[#FFFDF8] border border-[#2C2D2B]/10 rounded-xl shadow-lg p-2 animate-fade-in">
          <button
            onClick={() => window.open(googleUrl, "_blank")}
            className="block w-full text-left text-sm text-[#2C2D2B] hover:bg-[#F7F0E6] px-3 py-2 rounded-md"
          >
            Google Calendar
          </button>
          <button
            onClick={handleICalDownload}
            className="block w-full text-left text-sm text-[#2C2D2B] hover:bg-[#F7F0E6] px-3 py-2 rounded-md"
          >
            iCal (.ics)
          </button>
          <button
            onClick={() => window.open(outlookUrl, "_blank")}
            className="block w-full text-left text-sm text-[#2C2D2B] hover:bg-[#F7F0E6] px-3 py-2 rounded-md"
          >
            Outlook
          </button>
          <button
            onClick={() => window.open(yahooUrl, "_blank")}
            className="block w-full text-left text-sm text-[#2C2D2B] hover:bg-[#F7F0E6] px-3 py-2 rounded-md"
          >
            Yahoo Calendar
          </button>
        </div>
      )}
    </div>
  )
}
